import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Divider, Select, MenuItem, Card, Tooltip } from '@mui/material';
import { Key } from 'lucide-react';
import { makeStyles } from "@material-ui/core/styles";
import { LightenDarkenColor } from 'lighten-darken-color';
import { dataService } from '../../../../Services/data/data.service';
import DataListIcon from '../DataList.svg';
import { ChevronDown, Check, X, CircleOff, Save } from 'lucide-react';
import { toast } from 'react-toastify';

function MultiSelectDataList(props) {
    const [datalistValue, setDatalistValue] = useState("")
    const [listItems, setListItems] = useState([]);
    const classes = useStyles();

    const titleRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    // 
    const [lastSubmittedSelection, setLastSubmittedSelection] = useState([]);
    const [lastSubmittedTime, setLastSubmittedTime] = useState(null);

    // Dropdown
    const [openDropdown, setOpenDropdown] = useState(false);
    const openDropdownButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [dropdownWidth, setDropdownWidth] = useState(0);

    // Input
    const [inputValue, setInputValue] = useState("");

    // DataList
    const [allListItems, setAllListItems] = useState([]);
    const [filteredListItems, setFilteredListItems] = useState([]);
    const [selectedListItemIds, setSelectedListItemIds] = useState([]);

    useEffect(() => {
        const checkOverflow = () => {
            if (titleRef.current) {
                const element = titleRef.current;

                // Temporarily remove the clamp styles to measure natural height
                const originalStyle = element.style.webkitLineClamp;
                element.style.webkitLineClamp = "unset";
                element.style.display = "block";

                // Measure natural height
                const naturalHeight = element.getBoundingClientRect().height;

                // Restore original styles
                element.style.webkitLineClamp = originalStyle;
                element.style.display = "-webkit-box";

                // Get line height dynamically
                const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
                const maxHeight = lineHeight * 2; // Two lines

                setIsOverflowing(naturalHeight > maxHeight);
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [props.description]);

    const titleElement = (
        <h2 ref={titleRef} style={{ color: `${props.textColor} !important` }} className={classes.h2style}>
            {props.description}
        </h2>
    );

    useEffect(() => {
        dataService.getListItemsByListIdAndProjectGuid(props.dataListId, props.projectGuid).then((listItems) => {
            listItems.forEach((item) => {
                item.checked = false; // Initialize selected property
            })

            setAllListItems(listItems.sort((a, b) => a.name.localeCompare(b.name)));
        });

        setDatalistValue("")
        setInputValue("")
        setSelectedListItemIds([]);
        setLastSubmittedTime(null);
        setLastSubmittedSelection([]);
        setOpenDropdown(false);
        // eslint-disable-next-line 
    }, [props.refresh])

    useEffect(() => {
        if (openDropdown && openDropdownButtonRef.current) {
            const rect = openDropdownButtonRef.current.getBoundingClientRect();

            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4, // Adjust for scroll position
                left: rect.left + window.scrollX, // Adjust for horizontal scrolling
            });

            setDropdownWidth(rect.width);
        }
    }, [openDropdown]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && openDropdown) {
                // check if target is not the button
                if (!openDropdownButtonRef.current.contains(event.target)) {
                    setOpenDropdown(false);
                };
            };
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    const handleInputValue = (value) => {
        if (value !== "") {
            setOpenDropdown(true);
        }
        setInputValue(value);
    }

    // Handle what happens after checking or unchecking a company in the dropdown list
    const handleListItemSelection = (listItem) => {

        // find company in allUserCompanies set checked to opposite and if this is a deselect, remove from selectedCompanyIds else add to selectedCompanyIds
        let listItems = allListItems.map((item) => {
            if (item.id === listItem.id) {
                item.checked = !item.checked;
            }

            return item;
        });

        setAllListItems(listItems);

        sendData(listItems);

        if (!listItem.checked) {
            handleNewListItemSelection(selectedListItemIds.filter((id) => id !== listItem.id));
            // SetSelectedCompanyIds(selectedCompanyIds.filter((id => id !== company.id)));
        } else {
            handleNewListItemSelection([...selectedListItemIds, listItem.id]);
            // SetSelectedCompanyIds([...selectedCompanyIds, company.id]);
        };
    };

    // Handle what happens with the new list of selected companies
    const handleNewListItemSelection = (newListItemSelection) => {
        setSelectedListItemIds(newListItemSelection);
        // if newCompanySelection is empty, setCompanyIds to all ids of allUserCompanies
        if (newListItemSelection.length === 0) {
            let allListItemIds = allListItems.map((listItem) => listItem.id);
            // props.actions.setCompanyIds(allCompanyIds);
        } else {
            // props.actions.setCompanyIds(newListItemSelection);
        }
    };

    const resetListItemSelection = () => {
        let listItems = allListItems.map((item) => {
            item.checked = false; // Initialize selected property
            return item;
        });

        sendData(listItems);

        setAllListItems(listItems.sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedListItemIds([]);
    }

    const resetComponent = () => {
        let listItems = allListItems.map((item) => {
            item.checked = false; // Initialize selected property
            return item;
        });
        setAllListItems(listItems.sort((a, b) => a.name.localeCompare(b.name)));

        setDatalistValue("");
        setInputValue("");
        setSelectedListItemIds([]);
        setLastSubmittedTime(null);
        setLastSubmittedSelection([]);
        setOpenDropdown(false);
    }

    const handleSelect = (e) => {
        setDatalistValue(e.target.value)
        props.setDataValue(e.target.value, props.id)
    }

    const sendData = (listItems) => {
        if (props.keyRequired && !props.keyFound && !props.isKey) {
            resetComponent();
            toast.error("Submit the key value first", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            return;
        };
        const tempAllListItems = listItems ? listItems : allListItems;
        // get an alphabetic string of all selected list items, separated by commas
        let selectedListItems = tempAllListItems.filter((item) => item.checked).map((item) => item.name).join(", ");
        // set the value of the input field to the selected list items
        setLastSubmittedSelection(selectedListItems);
        props.setDataValue(selectedListItems, props.id);
        setLastSubmittedTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }

    return (
        <Card>
            <div className={classes.freeText} style={{ color: props.textColor, backgroundColor: `${props.color}` }}>
                <div className={classes.headerDiv} style={{ color: `${props.textColor} !important` }}>
                    {props.isKey && <Key className={classes.keyStyle}></Key>}
                    {isOverflowing ? (
                        <Tooltip title={props.description} placement="top" arrow>
                            {titleElement}
                        </Tooltip>
                    ) : (
                        titleElement
                    )}
                    <img className={classes.imageStyle} style={{ filter: props.darkness ? 'invert(1)' : 'contrast(80%)' }} src={DataListIcon} alt="" />
                </div>
                <Divider></Divider>
                <div className={classes.container} style={{ color: props.textColor, backgroundColor: LightenDarkenColor(props.color, 20) }}>
                    <div
                        ref={openDropdownButtonRef}
                        className={classes.inputContainer}
                        style={{ borderColor: "#B8B8B8" }}
                    // onClick={() => setOpenDropdown(!openDropdown)}
                    >
                        <input
                            className={classes.input}
                            value={inputValue}
                            placeholder="List-items"
                            onChange={(e) => handleInputValue(e.target.value)} // Allow typing in input
                            onClick={(e) => setOpenDropdown(true)} // Prevent div click from toggling dropdown
                        />
                        {selectedListItemIds.length > 0 &&
                            <div className={classes.selectionSummaryContainer}>
                                <p className={classes.selectionCount}>{selectedListItemIds.length}</p>
                                <button className={classes.resetButton} onClick={() => resetListItemSelection()}><X className={classes.resetIcon} /> </button>
                            </div>
                        }
                        <ChevronDown onClick={() => setOpenDropdown(!openDropdown)} className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? '180deg' : '0deg'})` }} />
                    </div>
                    {openDropdown &&
                        ReactDOM.createPortal(
                            <div
                                ref={dropdownRef}
                                className={classes.dropdownContainer}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownWidth }}
                            >
                                {/* filter on inputvalue */}
                                {allListItems.filter(listItem => listItem.name.toLowerCase().includes(inputValue.toLowerCase())).length > 0 ?
                                    allListItems.filter(listItem => listItem.name.toLowerCase().includes(inputValue.toLowerCase())).map((listItem, index) => (
                                        <button className={classes.dropdown__menuItem} onClick={() => handleListItemSelection(listItem)} key={"Type_" + index}>
                                            <div className={`${classes.checkbox} ${listItem.checked ? classes.checked : ''}`}>
                                                {listItem.checked ? <Check className={classes.checkboxIcon} /> : null}
                                            </div>
                                            {listItem.name}
                                        </button>
                                    ))
                                    :
                                    <div className={classes.dropdown__emptyState}>
                                        <CircleOff className={classes.emptyState__icon} />
                                        <p className={classes.emptyState__text}>There are no items to select</p>
                                    </div>
                                }
                            </div>,
                            document.body
                        )
                    }
                </div>
                <Divider></Divider>
                <div className={classes.StyleDiv}>
                </div>
            </div>
        </Card >
    );
}


export default MultiSelectDataList;

const useStyles = makeStyles({

    freeText: {
        overflow: 'hidden',
        backgroundRepeat: "no-repeat",
        backgroundSize: "10rem 10rem",
        backgroundPosition: "center",
        height: "12rem",

    },
    headerDiv: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '33%',
    },
    h2style: {
        margin: '0',
        zIndex: '2',
        maxWidth: 'calc(100% - 150px)',

        // break words if they are too long
        whiteSpace: 'normal',
        textOverflow: 'ellipsis',
        wordWrap: 'break-word',

        // max two lines
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    imageStyle: {
        position: 'absolute',
        right: '3%',
        opacity: '50%',
        width: '50px'
    },
    keyStyle: {
        position: 'absolute',
        left: '3%',
        opacity: '50%',
        width: '50px'
    },
    textDiv: {
        paddingLeft: '15px',
        paddingRight: '15px',
        display: "flex",
        justifyContent: "center",
        height: '34%',
        alignItems: 'center',
    },
    StyleDiv: {
        height: '33%',
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-evenly",
        alignItems: 'center',
    },

    container: {
        height: '34%',
        width: "100%",

        position: "relative",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",

        paddingLeft: '15px',
        paddingRight: '15px',
    },

    inputContainer: {
        minWidth: "fit-content",
        width: "100%",
        height: "fit-content",
        boxSizing: "border-box",

        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        border: "1px solid",
        borderRadius: "4px",
        backgroundColor: "transparent",

        padding: "0px",

        transition: "border-color 0.2s",

        "&:hover": {
            border: "1px solid #0C0E29",
            cursor: "cursor",
        },
    },

    input: {
        width: "100%",
        height: "56px",
        padding: "0 14px",
        paddingRight: "32px",
        border: "none",
        borderRadius: "4px",
        outline: "none",
        backgroundColor: "transparent",
        color: "#0C0E29",
        fontSize: "16px",
        textAlign: "left",

        "&:hover": {
            cursor: "text",
        },
    },

    selectionSummaryContainer: {
        height: "24px",
        width: "fit-content",
        position: "absolute",
        right: "38px",
        top: "50%",
        transform: "translateY(-50%)",


        display: "flex",
        gap: "8px",
        alignItems: "center",

        borderRadius: "4px",

        backgroundColor: "#C3996B",
        color: "#F8F8F8",

        paddingLeft: "4px",
    },

    selectionCount: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        margin: "0",
    },

    resetButton: {
        width: "24px",
        height: "24px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        padding: "4px",
        borderRadius: "4px",
        border: "none",
        backgroundColor: "transparent",
        color: "#F8F8F8",
        cursor: "pointer",

        transition: "color 0.2s",

        "&:hover": {
            color: "#D84949",
        },
    },

    resetIcon: {
        width: "16px",
        height: "16px",
        color: "inherit",
    },

    dropdownContainer: {
        width: '100%',
        minWidth: "160px",
        maxHeight: '200px',
        overflowY: 'auto',
        position: 'absolute',
        // top: 'calc(100% + 4px)',
        // left: '0',
        zIndex: '9999',
        backgroundColor: '#FFFFFF',
        borderRadius: '4px',
        border: '1px solid #DCDCDC',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',

        display: 'flex',
        flexDirection: 'column',

        padding: '4px',

        "&::-webkit-scrollbar": {
            width: "4px",
        },

        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#989898",
            borderRadius: "2px",
        },

        "&::-webkit-scrollbar-track": {
            backgroundColor: "#F4F4F4",
            borderRadius: "2px",
        },

        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#7C7C7C",
        },
    },

    dropdown__collapseButton: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        gap: "8px",
        justifyContent: 'space-between',
        alignItems: 'center',

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "rgba(12, 14, 41, 0.6)",
        backgroundColor: '#FFFFFF',

        // no text wrapping
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        "&:hover": {
            backgroundColor: '#F4F4F4',
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },
    },

    collapseButton__icon: {
        width: "16px",
        height: "16px",
        color: "#0C0E29",
    },

    dropdown__menuItem: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',
        gap: "8px",

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",
        backgroundColor: '#FFFFFF',

        "&:hover": {
            backgroundColor: '#F4F4F4',
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },
    },

    checkbox: {
        width: "16px",
        height: "16px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        borderRadius: "4px",
        border: "1px solid #0C0E29",
        backgroundColor: "transparent",
    },

    checked: {
        border: "none",
        backgroundColor: "#C3996B",
        color: "#F8F8F8",
    },

    checkboxIcon: {
        height: "12px",
        width: "12px",

        strokeWidth: "2px",

        color: "inherit",
    },

    dropdown__createButton: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',
        gap: "8px",

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",
        backgroundColor: '#F4F4F4',

        "&:hover": {
            backgroundColor: '#EDEDED',

            // style chip grey on hover
            "& > div": {
                backgroundColor: '#DCDCDC',
            },
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },


    },

    chip__grey: {
        width: "fit-content",
        height: "22px",
        display: "inline-flex",
        alignItems: "center",
        padding: "0px 8px",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        borderRadius: "12px",
        backgroundColor: "#EDEDED",
        color: "#0C0E29",
        transition: "background-color 0.2s",
    },

    dropdown__emptyState: {
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "24px 0 16px 0",
    },

    emptyState__icon: {
        width: "24px",
        height: "24px",
        color: "#0C0E29",

        strokeWidth: "1.4px",
    },

    emptyState__text: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",
        margin: "0",
    },

    input__iconButton: {
        height: "32px",

        position: "absolute",
        right: "4px",
        top: "4px",

        display: "flex",
        gap: "8px",
        alignItems: "center",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "#0C0E29",
        backgroundColor: "transparent",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#F8F8F8",
            color: "#D84949",
            cursor: "pointer",
        }
    },

    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },

    input__icon: {
        position: "absolute",
        right: "10px",
        width: "20px",
        height: "20px",
        color: "#0C0E29",

        "&:hover": {
            cursor: "pointer",
        },
    },

    icon: {
        width: "20px",
        height: "20px",
        strokeWidth: "1.4px",
        color: "#0C0E29",
    },

    inputRow: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },

    radioButtonContainer: {
        width: "100%",
        display: "flex",
        gap: "8px",
    },

    radioButton: {
        width: "fit-content",
        height: "40px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px",

        borderRadius: "4px",

        transition: "all ease-in-out 0.2s",

        "&:hover": {
            backgroundColor: "#EDEDED",
            cursor: "pointer",
        }
    },

    radio: {
        width: "20px",
        height: "20px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        boxSizing: "border-box",

        borderRadius: "50%",
        border: "1px solid #0C0E29",
        backgroundColor: "transparent",
    },

    radioIcon: {
        color: "#F8F8F8",
    },

    radioButton__label: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },

    primaryButton: {
        height: "40px",
        width: "fit-content",
        padding: "0 16px",
        paddingLeft: "10px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",

        border: "none",
        borderRadius: "4px",

        fontSize: "18px",
        color: "white",
        backgroundColor: "#C3996B",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            cursor: "pointer",
        }
    },

    buttonIcon: {
        height: "20px",
        width: "20px",

        display: "block",
        strokeWidth: "1.8px",
    },

    disabledButton: {
        backgroundColor: "#DCDCDC !important",
        color: "#989898 !important",

        "&:hover": {
            cursor: "not-allowed",
        }
    },
});